/**
 * useBoothTheme — owns the active booth atmosphere.
 *
 * Reads the saved `booth_os_theme` from sessionStorage on mount and
 * persists every change so a tablet refresh holds the same
 * atmosphere for the night. Returns the resolved theme object plus
 * the raw id and a setter — exactly the surface the BoothOSPage
 * previously inlined.
 *
 * Behaviour preserved verbatim:
 *   • storage key  : 'booth_os_theme'
 *   • storage scope: sessionStorage (per-tab, not cross-night)
 *   • fallback     : DEFAULT_THEME_ID from boothOSThemes
 *
 * Safe in environments without sessionStorage (private browsing,
 * older devices) — both reads and writes are wrapped in try/catch
 * with a silent default.
 */
import { useEffect, useState } from 'react';
import { DEFAULT_THEME_ID, getTheme } from '../lib/boothOSThemes';

const STORAGE_KEY = 'booth_os_theme';

export function useBoothTheme() {
  const [themeId, setThemeId] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || DEFAULT_THEME_ID; }
    catch { return DEFAULT_THEME_ID; }
  });

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, themeId); } catch { /* silent */ }
  }, [themeId]);

  return {
    themeId,
    setThemeId,
    theme: getTheme(themeId),
  };
}
